<template>
  <div class="mt-auto border-t-1 border-gray-border p-[15px]">
    <div class="flex items-center gap-3 lg:gap-5">
      <div class="flex items-center w-full min-w-0 gap-3 lg:gap-5">
        <button
          type="button"
          :disabled="!isFileEnabled"
          class="shrink-0"
          :class="
            isFileEnabled
              ? 'cursor-pointer text-gray-light hover:text-gray-dark'
              : 'cursor-not-allowed text-gray-light/60'
          "
          :aria-label="fileActionLabel"
          @click="openFilePicker"
        >
          <SvgoIconComponentChatIconFile></SvgoIconComponentChatIconFile>
        </button>
        <input
          ref="fileInputRef"
          type="file"
          multiple
          class="hidden"
          @change="handleFileChange"
        />

        <textarea
          ref="textareaRef"
          v-model="messageValue"
          rows="1"
          :maxlength="MESSAGE_MAX_LENGTH"
          @input="applyTextareaHeight"
          @keydown="handleTextareaKeydown"
          @paste="handleTextareaPaste"
          class="w-full min-h-[26px] max-h-[130px] resize-none overflow-y-hidden bg-transparent border-0 p-0 m-0 font-helvetica-400 font-normal text-[16px] leading-[22px] text-gray-dark placeholder:text-gray-light outline-none lg:text-[18px] lg:leading-[26px]"
          placeholder="Задайте свой вопрос"
        />
      </div>

      <button
        type="button"
        :disabled="!isSendEnabled"
        class="shrink-0"
        :class="isSendEnabled ? 'cursor-pointer' : 'cursor-not-allowed'"
        aria-label="Отправить сообщение"
        @click="handleSend"
      >
        <SvgoIconComponentChatIconArrow class="shrink-0 transition-colors" :class="
            isSendEnabled
              ? 'text-green-button hover:text-gray-dark'
              : 'text-gray-light'
          "></SvgoIconComponentChatIconArrow>
      </button>
    </div>

    <div v-if="fileWarningText" class="mt-2 flex pl-[44px]">
      <p
        class="shadow-danger-soft rounded-[10px] bg-danger-soft px-3 py-2 font-helvetica-400 text-[12px] leading-[16px] text-danger"
      >
        {{ fileWarningText }}
      </p>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, nextTick, onMounted, ref, watch } from "vue";

const props = withDefaults(
  defineProps<{
    modelValue?: string;
    sending?: boolean;
    uploading?: boolean;
    canUploadFile?: boolean;
    fileWarningText?: string;
  }>(),
  {
    modelValue: "",
    sending: false,
    uploading: false,
    canUploadFile: false,
    fileWarningText: "",
  },
);

const emit = defineEmits<{
  (e: "update:modelValue", value: string): void;
  (e: "send"): void;
  (e: "attachFiles", files: File[]): void;
}>();

const textareaRef = ref<HTMLTextAreaElement | null>(null);
const fileInputRef = ref<HTMLInputElement | null>(null);

const TEXTAREA_MIN_HEIGHT = 26;
const TEXTAREA_MAX_HEIGHT = 130;
const MESSAGE_MAX_LENGTH = 500;

const messageValue = computed({
  get: () => props.modelValue,
  set: (value: string) =>
    emit("update:modelValue", value.slice(0, MESSAGE_MAX_LENGTH)),
});

const isSendEnabled = computed(
  () =>
    props.modelValue.trim().length > 0 && !props.sending && !props.uploading,
);
const isFileEnabled = computed(
  () => Boolean(props.canUploadFile) && !props.sending && !props.uploading,
);
const fileActionLabel = computed(() =>
  props.canUploadFile
    ? props.uploading
      ? "Файл загружается"
      : "Прикрепить файл"
    : "Сначала отправьте сообщение",
);

const applyTextareaHeight = () => {
  const textarea = textareaRef.value;
  if (!textarea) return;

  textarea.style.height = `${TEXTAREA_MIN_HEIGHT}px`;

  const nextHeight = Math.min(textarea.scrollHeight, TEXTAREA_MAX_HEIGHT);
  textarea.style.height = `${Math.max(TEXTAREA_MIN_HEIGHT, nextHeight)}px`;
  textarea.style.overflowY =
    textarea.scrollHeight > TEXTAREA_MAX_HEIGHT ? "auto" : "hidden";
};

const handleSend = () => {
  if (!isSendEnabled.value) return;
  emit("send");
};

const openFilePicker = () => {
  if (!isFileEnabled.value) return;
  const input = fileInputRef.value;
  if (!input) return;
  input.value = "";
  input.click();
};

const handleFileChange = (event: Event) => {
  const target = event.target as HTMLInputElement | null;
  if (!target) return;
  const files = Array.from(target?.files ?? []);
  if (!files.length) return;
  emit("attachFiles", files);
  target.value = "";
};

const extractClipboardFiles = (clipboardData?: DataTransfer | null) => {
  if (!clipboardData) return [];

  const directFiles = Array.from(clipboardData.files ?? []);
  if (directFiles.length) return directFiles;

  return Array.from(clipboardData.items ?? [])
    .filter((item) => item.kind === "file")
    .map((item) => item.getAsFile())
    .filter((file): file is File => Boolean(file));
};

const handleTextareaKeydown = (event: KeyboardEvent) => {
  if (event.key !== "Enter" || event.shiftKey || event.isComposing) return;
  event.preventDefault();
  handleSend();
};

const handleTextareaPaste = (event: ClipboardEvent) => {
  const files = extractClipboardFiles(event.clipboardData);
  if (!files.length) return;
  event.preventDefault();
  emit("attachFiles", files);
};

watch(
  () => props.modelValue,
  () => {
    nextTick(applyTextareaHeight);
  },
);

onMounted(() => {
  nextTick(applyTextareaHeight);
});
</script>
